// frontend/src/pages/StudentsPage.js
import { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Table, Space, message, Popconfirm, Typography, FloatButton, Select, Input, Card, List, Alert, Modal, Form, Upload, Dropdown, Row, Col, Divider, Menu, Popover, Badge, Tag, Tabs, Segmented } from 'antd';
import {
    PlusOutlined, UploadOutlined, InboxOutlined, EditOutlined, DeleteOutlined,
    EyeOutlined, SortAscendingOutlined, MoreOutlined, DownloadOutlined,
    TeamOutlined, LoadingOutlined, ManOutlined, WomanOutlined, FilterOutlined,
    SearchOutlined, UndoOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getAllStudents, deleteStudent, restoreStudent, assignRollNumbers, importStudents, bulkUpdateStudents, exportStudentsAsCSV } from '../services/studentService';
import useResponsive from '../hooks/useResponsive';
import { getAllClasses } from '../services/classService';
import api from '../services/api';
import femaleAvatar from '../assets/female.png';
import maleAvatar from '../assets/male.png';



const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// Define the initial page size for mobile rendering
const MOBILE_PAGE_SIZE = 20;

const StudentsPage = () => {
    const navigate = useNavigate();
    const { classId: urlClassId } = useParams();
    const { isMobile } = useResponsive();
    const { currentYear } = useAcademicYear();
    const { user } = useAuth();
    const [dataSource, setDataSource] = useState([]); // Holds all fetched data
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in StudentsPage:", err);
            }
        };
        fetchSettings();
    }, []);

    const isViewDisabled = false; // Always allow entering the page to view assigned students
    const isEditDisabled = user?.role === 'teacher' && settings?.allow_teacher_student_edit === false && !isClassTeacherOfSelected;
    // Initialize state from URL if present
    const [selectedClassFilter, setSelectedClassFilter] = useState(urlClassId ? parseInt(urlClassId) : null);
    const [selectedGenderFilter, setSelectedGenderFilter] = useState(null); // NEW: Gender Filter State
    const [selectedSecondLanguageFilter, setSelectedSecondLanguageFilter] = useState(null);
    const [selectedPovertyStatusFilter, setSelectedPovertyStatusFilter] = useState(null); // APL/BPL
    const [selectedParentStatusFilter, setSelectedParentStatusFilter] = useState(null); // Father/Mother not alive
    const [selectedMarkFilter, setSelectedMarkFilter] = useState(null); // Marks above 90% / below 30%
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeletedOnly, setShowDeletedOnly] = useState(false);
    const [classes, setClasses] = useState([]);
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [importClassId, setImportClassId] = useState(undefined);
    const [importLoading, setLoadingImport] = useState(false);
    const [fileList, setFileList] = useState([]);
    // bulk edit states
    const [isBulkEditing, setIsBulkEditing] = useState(false); // New: Track bulk edit mode
    const [editedStudents, setEditedStudents] = useState({}); // New: Store changes: { studentId: { admission_no: 'newAdm', roll_no: 12 } }
    const [isSavingBulk, setIsSavingBulk] = useState(false); // New: Loading state for bulk save
    const [validationErrors, setValidationErrors] = useState({}); // New: For bulk edit validation
    const [isFilterPopoverVisible, setIsFilterPopoverVisible] = useState(false);
    const [isExporting, setIsExporting] = useState(false); // New: Loading state for export

    // State for mobile progressive loading
    const [displayedCount, setDisplayedCount] = useState(MOBILE_PAGE_SIZE);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15 });
    const [userTutorGroup, setUserTutorGroup] = useState([]);
    const isPrincipalOrAdmin = user?.role === 'principal' || user?.role === 'super_admin';

    // Check if current user is the class teacher of the selected class
    const isClassTeacherOfSelected = useMemo(() => {
        if (!selectedClassFilter || !classes.length) return false;
        const selectedClass = classes.find(c => c.id === selectedClassFilter);
        return selectedClass?.class_teacher_id === user?.id;
    }, [selectedClassFilter, classes, user?.id]);

    // Determine if user can import/export (must be principal/admin or class teacher of selected class, and class must be selected)
    const canImportExport = useMemo(() => {
        const isEditAllowedAll = settings?.allow_teacher_student_edit !== false;
        return selectedClassFilter && (isPrincipalOrAdmin || isEditAllowedAll || isClassTeacherOfSelected);
    }, [selectedClassFilter, isPrincipalOrAdmin, isClassTeacherOfSelected, settings]);

    // Initialize filters and pagination on mount or when URL param changes
    useEffect(() => {
        // Reset everything to defaults first, then apply URL params
        setPagination({ current: 1, pageSize: 15 });
        setSelectedGenderFilter(null);
        setSelectedSecondLanguageFilter(null);
        setSelectedPovertyStatusFilter(null);
        setSelectedParentStatusFilter(null);
        setSelectedMarkFilter(null);
        setSearchTerm('');
        setDisplayedCount(MOBILE_PAGE_SIZE);

        if (urlClassId) {
            setSelectedClassFilter(parseInt(urlClassId));
        } else {
            setSelectedClassFilter(null);
        }

    }, [urlClassId]); // Re-run if URL param changes

    const fetchCounter = useRef(0);

    // Refetch data when filters change OR when currentYear is available
    useEffect(() => {
        if (currentYear) {
            fetchData();
        }
    }, [currentYear, selectedClassFilter, selectedGenderFilter, selectedPovertyStatusFilter, selectedParentStatusFilter, selectedMarkFilter, selectedSecondLanguageFilter, showDeletedOnly]);

    const fetchData = async () => {
        fetchCounter.current += 1;
        const currentFetchId = fetchCounter.current;

        setLoading(true);
        setDisplayedCount(MOBILE_PAGE_SIZE);
        try {
            const filters = {
                classId: selectedClassFilter,
                gender: selectedGenderFilter,
                secondLanguage: selectedSecondLanguageFilter,
                povertyStatus: selectedPovertyStatusFilter,
                parentStatus: selectedParentStatusFilter,
                markFilter: selectedMarkFilter,
                is_deleted: showDeletedOnly
            };
            const [studentData, classData] = await Promise.all([
                getAllStudents(filters),
                getAllClasses()
            ]);
            
            if (currentFetchId === fetchCounter.current) {
                const sortedStudentData = studentData.sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
                setDataSource(sortedStudentData.map(student => ({ ...student, key: student.id })));
                setClasses(classData);
            }
        } catch (error) {
            if (currentFetchId === fetchCounter.current) {
                message.error('Failed to fetch data. Please refresh the page.');
            }
        } finally {
            if (currentFetchId === fetchCounter.current) {
                setLoading(false);
            }
        }
    };

    // Function to load more items in mobile view
    const loadMore = () => {
        setDisplayedCount(prev => prev + MOBILE_PAGE_SIZE);
    };

    const handleDelete = async (id) => {
        try {
            await deleteStudent(id);
            message.success('Student moved to deleted students list!');
            setShowDeletedOnly(true);
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to delete student.');
        }
    };

    const handleRestore = async (id) => {
        try {
            await restoreStudent(id);
            message.success('Student restored successfully!');
            setShowDeletedOnly(false);
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to restore student.');
        }
    };

    const handleViewDetails = (id) => {
        navigate(`/students/${id}`);
    }

    const handleAssignRollNumbers = async () => {
        if (!selectedClassFilter) {
            message.error('Please select a class to assign roll numbers.');
            return;
        }

        const studentsInClass = dataSource.filter(student => student.class_id === selectedClassFilter);
        if (studentsInClass.length === 0) {
            message.info('There are no students in the selected class to assign roll numbers.');
            return;
        }
        const sortedStudents = studentsInClass.sort((a, b) => (a.student_name || '').localeCompare(b.student_name || ''));
        const enrollmentsPayload = sortedStudents.map((student, index) => ({ student_id: student.id, roll_no: index + 1 }));

        try {
            await assignRollNumbers(selectedClassFilter, enrollmentsPayload);
            message.success('Roll numbers have been assigned alphabetically.');
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to assign roll numbers.');
        }
    };

    const handleOpenImportModal = () => {
        setImportClassId(undefined);
        setFileList([]);
        setIsImportModalVisible(true);
    };

    const handleImportSubmit = async () => {
        if (!importClassId) {
            message.error('Please select a class for enrollment.');
            return;
        }
        if (fileList.length === 0) {
            message.error('Please select a CSV/Excel file.');
            return;
        }
        const formData = new FormData();
        formData.append('studentFile', fileList[0].originFileObj);
        setLoadingImport(true);
        try {
            await importStudents(formData, importClassId);
            message.success('Students uploaded and being processed!');
            setIsImportModalVisible(false);
            fetchData();
        } catch (error) {
            message.error(error.response?.data?.error || 'Import failed. Check file format.');
        } finally {
            setLoadingImport(false);
        }
    };

    const handleExportStudents = async () => {
        if (!selectedClassFilter) {
            message.error('Please select a class to export students.');
            return;
        }

        setIsExporting(true);
        try {
            await exportStudentsAsCSV(selectedClassFilter);
            message.success('Students exported successfully!');
        } catch (error) {
            message.error(error.response?.data?.error || 'Failed to export students.');
            console.error('Export error:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleBulkEditToggle = () => {
        // Toggle the bulk edit mode. Clear temporary edits when exiting.
        if (isBulkEditing) {
            setEditedStudents({});
            setValidationErrors({}); // Clear validation errors on cancel
        }
        setIsBulkEditing(!isBulkEditing);
    };

    const handleCellChange = (id, dataIndex, value) => {
        // 1. Update the state for what's being edited
        const newEditedStudents = {
            ...editedStudents,
            [id]: {
                ...editedStudents[id],
                [dataIndex]: value,
            }
        };
        setEditedStudents(newEditedStudents);

        // 2. Perform validation based on the new state
        const newErrors = { ...validationErrors };
        if (newErrors[id]) {
            delete newErrors[id][dataIndex]; // Clear previous error for this cell
            if (Object.keys(newErrors[id]).length === 0) {
                delete newErrors[id];
            }
        }

        const currentStudent = dataSource.find(s => s.id === id);
        if (!currentStudent) {
            setValidationErrors(newErrors);
            return;
        }

        const getEffectiveValue = (student, field) => {
            // Check against the potential new state of all edits
            return newEditedStudents[student.id]?.[field] ?? student[field];
        };

        let isDuplicate = false;
        // Validate Roll Number (must be unique within the same class)
        if (dataIndex === 'roll_no' && value) {
            isDuplicate = dataSource.some(student =>
                student.id !== id &&
                student.class_id === currentStudent.class_id &&
                getEffectiveValue(student, 'roll_no') === value
            );
            if (isDuplicate) {
                if (!newErrors[id]) newErrors[id] = {};
                newErrors[id].roll_no = `Roll no. is a duplicate in this class.`;
            }
        }

        // Validate Admission Number (must be unique globally)
        if (dataIndex === 'admission_no' && value) {
            isDuplicate = dataSource.some(student =>
                student.id !== id &&
                getEffectiveValue(student, 'admission_no') === value
            );
            if (isDuplicate) {
                if (!newErrors[id]) newErrors[id] = {};
                newErrors[id].admission_no = `Admission no. is already in use.`;
            }
        }

        setValidationErrors(newErrors);
    };

    const handleBulkSave = async () => {
        if (Object.keys(validationErrors).length > 0) {
            message.error('Please fix the validation errors before saving.');
            return;
        }
        if (Object.keys(editedStudents).length === 0) {
            message.warning('No changes to save.');
            return;
        }

        const payload = Object.keys(editedStudents).map(id => ({
            student_id: id,
            admission_no: editedStudents[id].admission_no,
            roll_no: editedStudents[id].roll_no,
            gender: editedStudents[id].gender,
            second_language: editedStudents[id].second_language,
        }));

        setIsSavingBulk(true);
        try {
            await bulkUpdateStudents(payload);
            message.success(`${payload.length} students updated successfully.`);
            setEditedStudents({});
            setIsBulkEditing(false); // Exit bulk edit mode
            fetchData(); // Refetch data to confirm changes and update 'key'
        } catch (error) {
            console.error('Bulk update error:', error);
            message.error('Failed to save bulk changes. Check server logs.');
        } finally {
            setIsSavingBulk(false);
        }
    };

    const handleClearFilters = () => {
        setSelectedClassFilter(null);
        setSelectedGenderFilter(null);
        setSelectedSecondLanguageFilter(null);
        setSelectedPovertyStatusFilter(null);
        setSelectedParentStatusFilter(null);
        setSelectedMarkFilter(null);
        setIsFilterPopoverVisible(false); // Close popover after clearing
    };

    const activeFilterCount = [
        selectedClassFilter,
        selectedGenderFilter,
        selectedSecondLanguageFilter,
        selectedPovertyStatusFilter,
        selectedParentStatusFilter,
        selectedMarkFilter,
    ].filter(Boolean).length;

    const renderFilterPopoverContent = () => (
        <div style={{ width: 280, padding: '12px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                    <Text strong>Class</Text>
                    <Select
                        placeholder="Filter by Class"
                        onChange={handleFilterChange(setSelectedClassFilter)}
                        allowClear
                        value={selectedClassFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {classesForFilter.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                    </Select>
                </div>
                <div>
                    <Text strong>Gender</Text>
                    <Select
                        placeholder="Filter by Gender"
                        onChange={handleFilterChange(setSelectedGenderFilter)}
                        allowClear
                        value={selectedGenderFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {genderOptions.map(g => <Option key={g.value} value={g.value}>{g.icon} {g.label}</Option>)}
                    </Select>
                </div>
                <div>
                    <Text strong>Second Language</Text>
                    <Select
                        placeholder="Filter by Second Language"
                        onChange={handleFilterChange(setSelectedSecondLanguageFilter)}
                        allowClear
                        value={selectedSecondLanguageFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {['None', 'Arabic', 'Malayalam', 'Sanskrit'].map(lang => <Option key={lang} value={lang}>{lang}</Option>)}
                    </Select>
                </div>
                <div>
                    <Text strong>Poverty Status</Text>
                    <Select
                        placeholder="APL / BPL"
                        onChange={handleFilterChange(setSelectedPovertyStatusFilter)}
                        allowClear
                        value={selectedPovertyStatusFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {povertyStatusOptions.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                    </Select>
                </div>
                <div>
                    <Text strong>Parent Status</Text>
                    <Select
                        placeholder="e.g., Father not alive"
                        onChange={handleFilterChange(setSelectedParentStatusFilter)}
                        allowClear
                        value={selectedParentStatusFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {parentStatusOptions.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                    </Select>
                </div>
                <div>
                    <Text strong>Performance</Text>
                    <Select
                        placeholder="e.g., Marks > 90%"
                        onChange={handleFilterChange(setSelectedMarkFilter)}
                        allowClear
                        value={selectedMarkFilter}
                        style={{ width: '100%', marginTop: 4 }}
                    >
                        {markFilterOptions.map(m => <Option key={m.value} value={m.value}>{m.label}</Option>)}
                    </Select>
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <Button onClick={handleClearFilters} block>Clear All Filters</Button>
            </Space>
        </div>
    );


    const classesForFilter = useMemo(() => {
        if (user?.role === 'teacher' && settings?.allow_teacher_student_view === false) {
            const classIdsInDataSource = new Set(dataSource.map(s => s.class_id || s.classId));
            return classes
                .filter(c => classIdsInDataSource.has(c.id) || c.class_teacher_id === user.id)
                .map(c => ({ id: c.id, name: c.name }));
        }
        return classes.map(c => ({ id: c.id, name: c.name }));
    }, [classes, dataSource, user, settings]);

    // Gender options are static
    const genderOptions = [
        { label: 'Male', value: 'male', icon: <ManOutlined /> },
        { label: 'Female', value: 'female', icon: <WomanOutlined /> },
        { label: 'Other', value: 'other', icon: <MoreOutlined /> }
    ];

    // New filter options
    // const categoryOptions = [
    //     { label: 'SC', value: 'SC' },
    //     { label: 'ST', value: 'ST' },
    // ];

    const povertyStatusOptions = [
        { label: 'APL', value: 'APL' },
        { label: 'BPL', value: 'BPL' },
    ];

    const parentStatusOptions = [
        { label: 'Father Not Alive', value: 'father_not_alive' },
        { label: 'Mother Not Alive', value: 'mother_not_alive' },
        { label: 'Both Not Alive', value: 'both_not_alive' },
    ];

    const markFilterOptions = [
        { label: 'Marks > 90%', value: 'above_90' },
        { label: 'Marks < 30%', value: 'below_30' },
    ];

    const filteredDataSource = useMemo(() => {
        // Recalculate filtered and sorted list
        let data = dataSource;
        // Class and gender filters are now handled by backend
        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            data = data.filter(s => (s.student_name && s.student_name.toLowerCase().includes(lowercasedSearch)) || (s.admission_no && s.admission_no.toLowerCase().includes(lowercasedSearch)) || (s.roll_no && String(s.roll_no).includes(lowercasedSearch)));
        }
        // Sort alphabetically first, then by roll_no if defined
        return data.sort((a, b) => {
            const parseRoll = (r) => {
                if (r === null || r === undefined || r === '') return Infinity;
                const n = Number(r);
                return Number.isNaN(n) ? Infinity : n;
            };
            const aRoll = parseRoll(a.roll_no);
            const bRoll = parseRoll(b.roll_no);
            if (aRoll !== bRoll) return aRoll - bRoll;
            // fallback to alphabetical by name when roll numbers are equal/missing
            return (a.student_name || '').localeCompare(b.student_name || '');
        });
    }, [dataSource, searchTerm]);

    // Data sliced for mobile display
    const mobileDataSource = filteredDataSource.slice(0, displayedCount);
    const hasMore = displayedCount < filteredDataSource.length;


    // --- Desktop Columns ---
    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: 'Avatar',
                dataIndex: 'profile_picture_url',
                key: 'avatar',
                width: 70,
                align: 'center',
                render: (profile_picture_url, record) => {
                    const src = profile_picture_url
                        ? `${process.env.REACT_APP_API_URL || ''}${profile_picture_url}`
                        : (record.gender && record.gender.toLowerCase() === 'female' ? femaleAvatar : maleAvatar);
                    return (
                        <img
                            src={src}
                            alt="Profile"
                            style={{
                                width: 36,
                                height: 36,
                                objectFit: 'cover',
                                borderRadius: '50%',
                                border: '1px solid #eee',
                                background: '#fafafa'
                            }}
                        />
                    );
                }
            },
            {
                title: 'Roll No.',
                dataIndex: 'roll_no',
                key: 'roll_no',
                width: 100,
                sorter: (a, b) => (a.roll_no || 0) - (b.roll_no || 0),
                render: (text, record) => {
                    if (isBulkEditing) {
                        const error = validationErrors[record.id]?.roll_no;
                        return (
                            <>
                                <Input
                                    value={editedStudents[record.id]?.roll_no ?? text}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        // Allow clearing the input by setting to null if empty string
                                        handleCellChange(record.id, 'roll_no', inputValue);
                                    }}
                                    style={{ width: 80 }}
                                    status={error ? 'error' : ''}
                                />
                                {error && <Text type="danger" style={{ fontSize: 12, display: 'block' }}>{error}</Text>}
                            </>
                        );
                    }
                    return <Text>{text || 'N/A'}</Text>;
                },
            },
            {
                title: 'Admission No',
                dataIndex: 'admission_no',
                width: 150,
                sorter: (a, b) => (a.admission_no || '').localeCompare(b.admission_no || ''),
                render: (text, record) => {
                    if (isBulkEditing) {
                        const error = validationErrors[record.id]?.admission_no;
                        return (
                            <>
                                <Input
                                    value={editedStudents[record.id]?.admission_no ?? text}
                                    onChange={(e) => handleCellChange(record.id, 'admission_no', e.target.value)}
                                    status={error ? 'error' : ''}
                                />
                                {error && <Text type="danger" style={{ fontSize: 12, display: 'block' }}>{error}</Text>}
                            </>
                        );
                    }
                    return <Text>{text || 'N/A'}</Text>;
                },
            },
            {
                title: 'Student Name',
                dataIndex: 'student_name',
                sorter: (a, b) => (a.student_name || '').localeCompare(b.student_name || ''),
                defaultSortOrder: 'ascend',
                fixed: 'left',
                width: 250,
            },
            {
                title: 'Class',
                dataIndex: 'class_name',
                key: 'class_name',
                width: 150,
            },
            {
                title: 'Guardian Phone',
                dataIndex: 'guardian_phone_number',
                key: 'guardian_phone_number',
                width: 150,
                render: (text) => text || 'N/A',
            },
            {
                title: 'Gender',
                dataIndex: 'gender',
                key: 'gender',
                width: 100,
                render: (text, record) => {
                    if (isBulkEditing) {
                        return (
                            <Select
                                value={editedStudents[record.id]?.gender ?? text}
                                onChange={(value) => handleCellChange(record.id, 'gender', value)}
                                style={{ width: '100%' }}
                                allowClear
                                placeholder="Select Gender"
                            >
                                {genderOptions.map(g => <Option key={g.value} value={g.value}>{g.icon} {g.label}</Option>)}
                            </Select>
                        );
                    }
                    const gender = (text || '').toLowerCase();
                    if (gender === 'male') return <Space><ManOutlined style={{ color: '#1890ff' }} /> Male</Space>;
                    if (gender === 'female') return <Space><WomanOutlined style={{ color: '#eb2f96' }} /> Female</Space>;
                    if (gender === 'other') return <Space><MoreOutlined /> Other</Space>;
                    return <Text type="secondary">N/A</Text>;
                },
            },
            // {second language column,  dropdown to select ['None', 'Arabic', 'Malayalam', 'Sanskrit']}
            {
                title: 'Second Language',
                dataIndex: 'second_language',
                key: 'second_language',
                width: 150,
                render: (text, record) => {
                    if (isBulkEditing) {
                        return (
                            <Select
                                value={editedStudents[record.id]?.second_language ?? text}
                                onChange={(value) => handleCellChange(record.id, 'second_language', value)}
                                style={{ width: '100%' }}
                                allowClear
                                placeholder="Select Second Language"
                            >
                                {['None', 'Arabic', 'Malayalam', 'Sanskrit'].map(lang => (
                                    <Option key={lang} value={lang}>{lang}</Option>
                                ))}
                            </Select>
                        );
                    }
                    return <Text>{text || 'N/A'}</Text>;
                },
            },
            {
                title: 'Action',
                key: 'action',
                width: 150,
                hidden: isBulkEditing,
                align: 'center',
                fixed: 'right',
                render: (_, record) => {
                    const isClassTeacher = classes.find(c => c.id === record.class_id)?.class_teacher_id === user.id;
                    const isTutor = userTutorGroup.includes(record.id);

                    const canEdit = !isEditDisabled && (isPrincipalOrAdmin || isClassTeacher || isTutor);

                    if (showDeletedOnly) {
                        return (
                            <Space size="small">
                                <Button title="View Details" icon={<EyeOutlined />} onClick={() => handleViewDetails(record.id)} />
                                {canEdit && (
                                    <Popconfirm okType='default' title="Restore this student?" onConfirm={() => handleRestore(record.id)}>
                                        <Button title="Restore Student" icon={<UndoOutlined />} style={{ borderColor: '#52c41a', color: '#52c41a' }} />
                                    </Popconfirm>
                                )}
                            </Space>
                        );
                    }

                    if (canEdit) {
                        return (
                            <Space size="small">
                                <Button title="View Details" icon={<EyeOutlined />} onClick={() => handleViewDetails(record.id)} />
                                <Link to={`/students/edit/${record.id}`}><Button title="Edit Profile" icon={<EditOutlined />} /></Link>
                                <Popconfirm okType='default' title="Delete this student?" onConfirm={() => handleDelete(record.id)}><Button title="Delete Student" icon={<DeleteOutlined />} danger /></Popconfirm>
                            </Space>
                        );
                    }
                    return <Button title="View Details" icon={<EyeOutlined />} onClick={() => handleViewDetails(record.id)} />;
                },
            },
        ];
        return baseColumns.filter(column => !column.hidden);
    }, [isPrincipalOrAdmin, classes, user, userTutorGroup, isBulkEditing, editedStudents, showDeletedOnly]);


    // --- Mobile Card Action Menu Definition ---
    const getMobileActionMenu = (record) => {
        const isClassTeacher = classes.find(c => c.id === record.class_id)?.class_teacher_id === user.id;
        const isTutor = userTutorGroup.includes(record.id); // <-- NEW CHECK

        const canEditDelete = !isEditDisabled && (isPrincipalOrAdmin || isClassTeacher || isTutor); // <-- UPDATED CONDITION

        const items = [{ key: 'view', label: 'View Profile', icon: <EyeOutlined /> }];

        if (showDeletedOnly) {
            if (canEditDelete) {
                items.push({
                    key: 'restore',
                    label: 'Restore Student',
                    icon: <UndoOutlined />,
                    style: { color: '#52c41a' }
                });
            }
        } else {
            if (canEditDelete) {
                items.push({ key: 'edit', label: 'Edit Details', icon: <EditOutlined /> });
                items.push({
                    key: 'delete',
                    label: 'Delete Student',
                    icon: <DeleteOutlined />,
                    danger: true
                });
            }
        }

        return (
            <Menu
                onClick={({ key }) => {
                    if (key === 'view') handleViewDetails(record.id);
                    if (key === 'edit') navigate(`/students/edit/${record.id}`);
                    if (key === 'delete') {
                        Modal.confirm({
                            title: 'Are you sure?',
                            content: 'Delete this student?',
                            okText: 'Yes, Delete',
                            okType: 'danger',
                            cancelText: 'Cancel',
                            onOk() {
                                handleDelete(record.id);
                            },
                        });
                    }
                    if (key === 'restore') {
                        Modal.confirm({
                            title: 'Restore student?',
                            content: 'Move student back to active list?',
                            okText: 'Yes, Restore',
                            cancelText: 'Cancel',
                            onOk() {
                                handleRestore(record.id);
                            },
                        });
                    }
                }}
                items={items}
            />
        );
    };


    // --- Mobile Card Design (Refined with Load More) ---
    const renderMobileList = () => {
        if (loading && dataSource.length === 0) {
            return <div style={{ textAlign: 'center', padding: '20px' }}><LoadingOutlined style={{ fontSize: 24 }} /> <Text>Loading students...</Text></div>;
        }

        const getAvatarSrc = (item) => {
            return item.profile_picture_url
                ? `${process.env.REACT_APP_API_URL || ''}${item.profile_picture_url}`
                : (item.gender && item.gender.toLowerCase() === 'female' ? femaleAvatar : maleAvatar);
        };

        return (
            <List
                // Use the sliced data for rendering
                dataSource={mobileDataSource}
                renderItem={(item) => (
                    <List.Item
                        style={{ padding: '8px 16px', border: 'none' }}
                    >
                        <Card
                            hoverable
                            style={{ width: '100%', borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                            bodyStyle={{ padding: 16 }}
                            onClick={() => handleViewDetails(item.id)}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', gap: 16 }}>
                                    <img
                                        src={getAvatarSrc(item)}
                                        alt="Profile"
                                        style={{
                                            width: 56,
                                            height: 56,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                            border: '2px solid #fff',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                    <div>
                                        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                                            {item.student_name || 'N/A'}
                                        </Text>
                                        <Space size={[0, 8]} wrap style={{ marginBottom: 8 }}>
                                            <Tag color="blue">{item.class_name || 'Unassigned'}</Tag>
                                            {item.gender && (
                                                <Tag color={item.gender.toLowerCase() === 'female' ? 'magenta' : 'cyan'}>
                                                    {item.gender}
                                                </Tag>
                                            )}
                                        </Space>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                Roll No: <Text strong>{item.roll_no || '-'}</Text>
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                Adm No: <Text strong>{item.admission_no || '-'}</Text>
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                                <Dropdown overlay={() => getMobileActionMenu(item)} trigger={['click']} placement="bottomRight">
                                    <Button
                                        icon={<MoreOutlined style={{ fontSize: 20 }} />}
                                        type="text"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ margin: '-8px -8px 0 0' }}
                                    />
                                </Dropdown>
                            </div>
                        </Card>
                    </List.Item>
                )}
            >
                {/* Load More Button (same as before) */}
                {hasMore && (
                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        <Button onClick={loadMore} loading={loading} block shape="round" size="large">
                            Load {Math.min(MOBILE_PAGE_SIZE, filteredDataSource.length - displayedCount)} more
                        </Button>
                    </div>
                )}
                {!hasMore && filteredDataSource.length > 0 && (
                    <Divider plain style={{ margin: '24px 0' }}>End of List ({filteredDataSource.length} students)</Divider>
                )}
            </List>
        );
    };
    // --- End Mobile Card Design ---

    // --- Consolidated Action Menu for Header ---
    const headerActionMenu = useMemo(() => {
        let items = [];

        // When in bulk editing mode, add Save and Cancel actions
        if (isBulkEditing) {
            items.push({
                key: 'bulk_save',
                label: isSavingBulk ? 'Saving...' : 'Save All Changes',
                icon: isSavingBulk ? <LoadingOutlined /> : <DownloadOutlined />,
                onClick: handleBulkSave,
                danger: true,
                disabled: isSavingBulk || Object.keys(editedStudents).length === 0 || Object.keys(validationErrors).length > 0,
            });
            items.push({
                key: 'bulk_cancel',
                label: 'Cancel Changes',
                icon: <DeleteOutlined />,
                onClick: handleBulkEditToggle, // Toggling off will clear state
            });
        }

        // Add Assign Roll Numbers if filter is selected (applies in both modes)
        if (selectedClassFilter) {
            items.push({
                key: 'assign_roll',
                label: (
                    <Popconfirm
                        okType='default'
                        title="Assign Roll Numbers?"
                        description="Re-assign alphabetically for the selected class."
                        onConfirm={handleAssignRollNumbers}
                        okText="Yes"
                        cancelText="Cancel"
                    >
                        Assign Roll No.
                    </Popconfirm>
                ),
                icon: <SortAscendingOutlined />,
                onClick: (e) => { e.domEvent.stopPropagation(); },
            });
        }

        // Add Import action for mobile view (for desktop, it's a button)
        if (isMobile && !isBulkEditing && canImportExport) {
            items.push({
                key: 'import',
                label: 'Import Students (CSV/Excel)',
                icon: <UploadOutlined />,
                onClick: handleOpenImportModal,
            });
            items.push({
                key: 'export',
                label: 'Export Students (CSV)',
                icon: <DownloadOutlined />,
                onClick: handleExportStudents,
            });
        }

        return { items };
    }, [selectedClassFilter, isMobile, isBulkEditing, isSavingBulk, editedStudents, canImportExport]);

    // --- End Header Action Menu ---

    const handleFilterChange = (setter) => (value) => {
        setter(value);
        setDisplayedCount(MOBILE_PAGE_SIZE); // Reset count on any filter change
        setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <Title level={2} style={{ margin: 0 }}>Student Management</Title>
                <Segmented
                    value={showDeletedOnly ? 'deleted' : 'active'}
                    onChange={(value) => {
                        setShowDeletedOnly(value === 'deleted');
                        setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                    options={[
                        { label: 'Active Students', value: 'active' },
                        { label: 'Deleted Students', value: 'deleted' }
                    ]}
                    size="large"
                    style={{ background: '#f5f5f5', borderRadius: '8px', padding: '2px' }}
                />
            </div>

            {isViewDisabled ? (
                <Alert
                    message="Access Denied"
                    description="You do not have permission to view student details. This feature has been disabled by the administrator."
                    type="error"
                    showIcon
                    style={{ borderRadius: 12 }}
                />
            ) : (
                <>
                    {isMobile && !isEditDisabled && !showDeletedOnly ? (
                        <FloatButton
                            icon={<PlusOutlined />} // Add PlusOutlined icon
                            shape="square"
                            onClick={() => navigate('/students/add')} // Navigate to add student page
                            tooltip="Add New Student" // Add a tooltip
                            type="default" // Make it a primary button
                            style={{ marginBottom: 24 }}
                        />
                    ) : null}
                    <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: isMobile ? 12 : 16 }}>
                        {isMobile ? (
                            /* --- MOBILE VIEW --- */
                            <Row gutter={8} wrap={false} align="middle">
                                <Col flex="1">
                                    <Input
                                        placeholder="Search name, admission, or roll ..."
                                        value={searchTerm}
                                        size="small"
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onPressEnter={() => handleFilterChange(setSearchTerm)(searchTerm)}
                                        allowClear
                                    />
                                </Col>

                                <Col>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SearchOutlined />}
                                        onClick={() => handleFilterChange(setSearchTerm)(searchTerm)}
                                        loading={loading}
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Col>

                                <Col>
                                    <Popover
                                        content={renderFilterPopoverContent()}
                                        title="Filter Students"
                                        trigger="click"
                                        placement="bottomLeft"
                                        open={isFilterPopoverVisible}
                                        onOpenChange={setIsFilterPopoverVisible}
                                    >
                                        <Badge count={activeFilterCount}>
                                            <Button size="large" icon={<FilterOutlined />} />
                                        </Badge>
                                    </Popover>
                                </Col>

                            </Row>
                        ) : (
                            /* --- DESKTOP VIEW --- */
                            <Row gutter={16} align="middle">
                                <Col span={10}> {/* Search */}
                                    <Search
                                        loading={loading}
                                        placeholder="Search name, admission, or roll ..."
                                        enterButton
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onSearch={(value) => { handleFilterChange(setSearchTerm)(value); }}
                                        allowClear
                                    />
                                </Col>
                                <Col> {/* Filter Button */}
                                    <Popover
                                        content={renderFilterPopoverContent()}
                                        title="Filter Students"
                                        trigger="click"
                                        placement="bottomLeft"
                                        open={isFilterPopoverVisible}
                                        onOpenChange={setIsFilterPopoverVisible}
                                    >
                                        <Badge count={activeFilterCount}>
                                            <Button icon={<FilterOutlined />}>
                                                Filters
                                            </Button>
                                        </Badge>
                                    </Popover>
                                </Col>
                                <Col flex="1" style={{ textAlign: 'right' }}> {/* Action Buttons */}
                                    {isBulkEditing ? (
                                        <Space>
                                            <Button
                                                icon={<DeleteOutlined />}
                                                onClick={handleBulkEditToggle}
                                                disabled={isSavingBulk}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="primary"
                                                danger
                                                icon={isSavingBulk ? <LoadingOutlined /> : <DownloadOutlined />}
                                                onClick={handleBulkSave}
                                                loading={isSavingBulk}
                                                disabled={isSavingBulk || Object.keys(editedStudents).length === 0 || Object.keys(validationErrors).length > 0}
                                            >
                                                Save All Changes ({Object.keys(editedStudents).length})
                                            </Button>
                                        </Space>
                                    ) : (
                                        <Space>
                                            {!isEditDisabled && !showDeletedOnly && (
                                                <Button
                                                    type="default"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => navigate('/students/add')}
                                                    size="large" // Made the button larger
                                                >
                                                    Add New Student
                                                </Button>
                                            )}
                                            {canImportExport && !isEditDisabled && !showDeletedOnly && (
                                                <Button
                                                    icon={<UploadOutlined />}
                                                    onClick={handleOpenImportModal}
                                                >
                                                    Import
                                                </Button>
                                            )}
                                            {canImportExport && !showDeletedOnly && (
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleExportStudents}
                                                    disabled={isExporting}
                                                    loading={isExporting}
                                                >
                                                    Export
                                                </Button>
                                            )}
                                            {!isEditDisabled && !showDeletedOnly && (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={handleBulkEditToggle}
                                                >
                                                    Bulk Edit
                                                </Button>
                                            )}
                                            {selectedClassFilter && !isEditDisabled && !showDeletedOnly && (
                                                <Popconfirm
                                                    okType='default'
                                                    title="Assign Roll Numbers?"
                                                    description="Re-assign alphabetically for the selected class."
                                                    onConfirm={handleAssignRollNumbers}
                                                    okText="Yes, Assign"
                                                    cancelText="Cancel"
                                                >
                                                    <Button icon={<SortAscendingOutlined />}>
                                                        Assign Roll No.
                                                    </Button>
                                                </Popconfirm>
                                            )}
                                        </Space>
                                    )}
                                </Col>
                            </Row>
                        )}
                    </Card>

                    {isMobile ? (
                        renderMobileList()
                    ) : (
                        <Table
                            dataSource={filteredDataSource}
                            columns={columns}
                            loading={loading}
                            bordered
                            scroll={{ x: 1000 }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: filteredDataSource.length,
                                showSizeChanger: true,
                                pageSizeOptions: [10, 15, 25, 50, 100],
                                showTotal: (total) => `Total ${total} students`,
                            }}
                            onChange={(p) => {
                                setPagination({
                                    current: p.current,
                                    pageSize: p.pageSize,
                                });
                            }}
                        />
                    )}

                    <Modal
                        okType='default'
                        title="Import Students from CSV/Excel"
                        open={isImportModalVisible}
                        onCancel={() => setIsImportModalVisible(false)}
                        onOk={handleImportSubmit}
                        confirmLoading={importLoading}
                    >
                        <Form layout="vertical">
                            <Form.Item label="Enrollment Class" required>
                                <Select
                                    placeholder="Select the class for new students"
                                    onChange={setImportClassId}
                                    value={importClassId}
                                >
                                    {classes.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item label="CSV/Excel File (.csv, .xlsx)" required>
                                <Upload.Dragger
                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                    maxCount={1}
                                    beforeUpload={() => false} // Prevent default upload behavior
                                    fileList={fileList}
                                    onChange={({ fileList }) => setFileList(fileList)}
                                >
                                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                    <p className="ant-upload-text">Click or drag file to this area</p>
                                    <p className="ant-upload-hint">Support for CSV and Excel files only. Must include 'student_name' and 'admission_no' columns.</p>
                                </Upload.Dragger>
                            </Form.Item>
                        </Form>
                    </Modal>
                </>
            )}
        </div>
    );
};

export default StudentsPage;